import type { IconName } from '@circles/tokens';
import {
  Archive,
  BadgeCheck,
  Bell,
  BellOff,
  Bookmark,
  Building2,
  Camera,
  ChartBarBig,
  ChartNoAxesColumn,
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  CircleUserRound,
  Copy,
  Ellipsis,
  FileText,
  Forward,
  Hash,
  Heart,
  Image,
  KeyRound,
  Lock,
  LogOut,
  Mail,
  MailPlus,
  MapPin,
  MessageCircle,
  Mic,
  MicOff,
  MonitorSmartphone,
  Newspaper,
  Paperclip,
  Pause,
  Pencil,
  Phone,
  PhoneOff,
  Pin,
  Play,
  Plus,
  Repeat2,
  Reply,
  Search,
  SendHorizontal,
  Settings,
  Share,
  ShieldCheck,
  SmilePlus,
  SquarePen,
  SunMoon,
  SwitchCamera,
  Timer,
  Trash2,
  UserPlus,
  Users,
  Video,
  VideoOff,
  Volume2,
  X,
  type LucideIcon,
} from 'lucide-react';

/** Semantic icon names from web/tokens/src/icons.json, mapped to Lucide components. */
const map: Record<IconName, LucideIcon> = {
  chats: MessageCircle,
  feed: Newspaper,
  calls: Phone,
  you: CircleUserRound,
  search: Search,
  compose: SquarePen,
  add: Plus,
  back: ChevronLeft,
  chevronRight: ChevronRight,
  close: X,
  more: Ellipsis,
  send: SendHorizontal,
  attach: Paperclip,
  camera: Camera,
  photo: Image,
  document: FileText,
  microphone: Mic,
  microphoneOff: MicOff,
  emoji: SmilePlus,
  reply: Reply,
  forward: Forward,
  edit: Pencil,
  delete: Trash2,
  copy: Copy,
  pin: Pin,
  mute: BellOff,
  archive: Archive,
  tickSent: Check,
  tickDelivered: CheckCheck,
  play: Play,
  pause: Pause,
  phone: Phone,
  phoneOff: PhoneOff,
  video: Video,
  videoOff: VideoOff,
  speaker: Volume2,
  switchCamera: SwitchCamera,
  addPerson: UserPlus,
  group: Users,
  like: Heart,
  comment: MessageCircle,
  repost: Repeat2,
  bookmark: Bookmark,
  views: ChartNoAxesColumn,
  share: Share,
  poll: ChartBarBig,
  official: BadgeCheck,
  expiry: Timer,
  lock: Lock,
  key: KeyRound,
  shield: ShieldCheck,
  bell: Bell,
  devices: MonitorSmartphone,
  appearance: SunMoon,
  help: CircleHelp,
  logout: LogOut,
  mail: Mail,
  request: MailPlus,
  settings: Settings,
  external: Building2,
  location: MapPin,
  hashtag: Hash,
};

export interface IconProps {
  name: IconName;
  size?: number;
  filled?: boolean;
  strokeWidth?: number;
  className?: string;
  label?: string;
}

export function Icon({ name, size = 22, filled, strokeWidth = 1.8, className, label }: IconProps) {
  const Component = map[name];
  return (
    <Component
      size={size}
      strokeWidth={strokeWidth}
      className={className}
      fill={filled ? 'currentColor' : 'none'}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? 'img' : undefined}
    />
  );
}

export const iconComponents = map;
